// The radar scope: days since contact (log-ish rings) by investor type (8 sectors). Canvas, crisp at any DPR.
import { h } from "./dom";

export interface Blip {
  k: string;
  name: string;
  days: number; // days since last contact
  strength: number; // 0-5
  tone: "amber" | "red" | "green" | "cyan";
  type: string; // investor type
}

export const SECTORS8 = ["FAMILY OFFICE", "UHNW", "HNW", "INDEP SPONSOR", "PE FUND GP", "LP/ALLOC", "OPERATOR", "OTHER"];
const SECTOR_OF: Record<string, number> = {
  "Family office": 0, "Multi-family office": 0, "UHNW individual": 1, "HNW individual": 2, "Independent sponsor": 3,
  "PE fund GP": 4, "LP / fund allocator": 5, "Operator / executive": 6,
};
export const sectorIndex = (type: string): number => SECTOR_OF[type] ?? 7;

const RINGS: [number, string][] = [[30, "30D"], [90, "90D"], [180, "180D"], [365, "1Y"], [730, "2Y+"]];
const RING_STEP = 1 / 6; // five labeled rings out to 5/6 of the radius; the last sixth is "2Y+ and never"
/** Radius fraction (0-1) for a number of days: linear inside each ring band. */
export function radiusFrac(days: number): number {
  let prevD = 0;
  let prevR = 0;
  for (let i = 0; i < RINGS.length; i++) {
    const d = RINGS[i][0];
    const r = (i + 1) * RING_STEP;
    if (days <= d) return prevR + ((days - prevD) / (d - prevD)) * (r - prevR);
    prevD = d;
    prevR = r;
  }
  return Math.min(1, 5 * RING_STEP + Math.min(1, (days - 730) / 2000) * (1 - 5 * RING_STEP));
}

function hash(s: string): number {
  let x = 2166136261;
  for (let i = 0; i < s.length; i++) x = Math.imul(x ^ s.charCodeAt(i), 16777619);
  return ((x >>> 0) % 100000) / 100000;
}

export interface Scope {
  update(blips: Blip[]): void;
  select(k: string | null): void;
  destroy(): void;
  el: HTMLElement;
}

export function createScope(onSelect: (k: string) => void): Scope {
  const canvas = h("canvas", { "aria-label": "Radar scope: contacts by days since contact and investor type", role: "img" });
  const tip = h("div", { class: "tip hidden" });
  const el = h("div", { class: "scope" }, canvas, tip);
  const ctx2d = canvas.getContext("2d")!;
  let blips: Blip[] = [];
  let pos: { x: number; y: number; b: Blip; s: number }[] = [];
  let selected: string | null = null;
  let hover: string | null = null;
  let W = 400;
  let dpr = 1;
  let raf = 0;
  let sweep = Math.random() * Math.PI * 2;
  let last = 0;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
  const PAD = 68;

  const geom = () => ({ cx: W / 2, cy: W / 2, R: W / 2 - PAD });

  function layout() {
    const { cx, cy, R } = geom();
    pos = blips.map((b) => {
      const sec = sectorIndex(b.type);
      const r = radiusFrac(b.days) * R;
      // angle inside the sector, kept off the sector edges; 12 o'clock is the middle of sector 0's start edge
      const a0 = (sec / 8) * Math.PI * 2 - Math.PI / 2;
      const a = a0 + (0.08 + 0.84 * hash(b.k)) * ((Math.PI * 2) / 8);
      const s = 2.5 + b.strength * 0.7;
      return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r, b, s };
    });
  }

  function css(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  function draw() {
    const c = ctx2d;
    const col = { bg: css("--panel"), line: css("--line2"), hair: css("--line"), dim: css("--dim"), text: css("--text"), cyan: css("--cyan"), mg: css("--magenta"), green: css("--green"), amber: css("--amber"), red: css("--red") };
    const { cx, cy, R } = geom();
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, W, W);
    c.fillStyle = col.bg;
    c.fillRect(0, 0, W, W);
    c.lineWidth = 1;
    // sector spokes
    c.strokeStyle = col.hair;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
      c.beginPath();
      c.moveTo(cx + Math.cos(a) * R * 0.0, cy + Math.sin(a) * R * 0.0);
      c.lineTo(cx + Math.cos(a) * (R + 6), cy + Math.sin(a) * (R + 6));
      c.stroke();
    }
    // rings
    c.font = '9.5px "B612 Mono", monospace';
    c.textBaseline = "middle";
    RINGS.forEach((_r, i) => {
      const r = (i + 1) * RING_STEP * R;
      c.strokeStyle = col.hair;
      c.beginPath();
      c.arc(cx, cy, r, 0, Math.PI * 2);
      c.stroke();
    });
    c.strokeStyle = col.line;
    c.beginPath();
    c.arc(cx, cy, R, 0, Math.PI * 2);
    c.stroke();
    // tick marks on the outer ring every 30 degrees, minor every 10
    c.strokeStyle = col.line;
    for (let d = 0; d < 360; d += 10) {
      const a = (d * Math.PI) / 180;
      const len = d % 45 === 0 ? 7 : d % 30 === 0 ? 5 : 3;
      c.beginPath();
      c.moveTo(cx + Math.cos(a) * R, cy + Math.sin(a) * R);
      c.lineTo(cx + Math.cos(a) * (R + len), cy + Math.sin(a) * (R + len));
      c.stroke();
    }
    // sector labels
    c.fillStyle = col.dim;
    c.font = '9.5px "B612 Mono", monospace';
    c.textAlign = "center";
    SECTORS8.forEach((label, i) => {
      const a = ((i + 0.5) / 8) * Math.PI * 2 - Math.PI / 2;
      const lr = R + 14;
      const co = Math.cos(a);
      c.textAlign = co > 0.35 ? "left" : co < -0.35 ? "right" : "center";
      c.fillText(label, cx + co * lr, cy + Math.sin(a) * (lr + 2));
    });
    // sweep (the one gradient)
    if (!reduce.matches) {
      try {
        const wedge = c.createConicGradient(sweep - 0.9, cx, cy);
        wedge.addColorStop(0, hexA(col.cyan, 0));
        wedge.addColorStop(0.143, hexA(col.cyan, 0.13));
        wedge.addColorStop(0.1431, hexA(col.cyan, 0));
        wedge.addColorStop(1, hexA(col.cyan, 0));
        c.fillStyle = wedge;
        c.beginPath();
        c.arc(cx, cy, R, 0, Math.PI * 2);
        c.fill();
      } catch {
        /* older canvas: no sweep wedge */
      }
      c.strokeStyle = hexA(col.cyan, 0.55);
      c.beginPath();
      c.moveTo(cx, cy);
      c.lineTo(cx + Math.cos(sweep) * R, cy + Math.sin(sweep) * R);
      c.stroke();
    }
    // center mark
    c.strokeStyle = col.dim;
    c.beginPath();
    c.moveTo(cx - 4, cy); c.lineTo(cx + 4, cy); c.moveTo(cx, cy - 4); c.lineTo(cx, cy + 4);
    c.stroke();
    // blips
    for (const p of pos) {
      const color = p.b.tone === "amber" ? col.amber : p.b.tone === "red" ? col.red : p.b.tone === "cyan" ? col.cyan : col.green;
      c.fillStyle = color;
      c.fillRect(Math.round(p.x - p.s / 2), Math.round(p.y - p.s / 2), p.s, p.s);
    }
    c.font = '9.5px "B612 Mono", monospace';
    c.textBaseline = "middle";
    c.textAlign = "left";
    RINGS.forEach(([, label], i) => {
      const r = (i + 1) * RING_STEP * R;
      const tw = c.measureText(label).width;
      c.fillStyle = col.bg;
      c.fillRect(cx + 2, cy - r - 6, tw + 4, 11);
      c.fillStyle = col.dim;
      c.fillText(label, cx + 4, cy - r);
    });
    for (const p of pos) {
      if (p.b.k === selected || p.b.k === hover) {
        c.strokeStyle = col.mg;
        c.lineWidth = 1.5;
        const s = p.s + 7;
        c.strokeRect(Math.round(p.x - s / 2) + 0.5, Math.round(p.y - s / 2) + 0.5, s, s);
        c.lineWidth = 1;
      }
    }
  }

  function hexA(color: string, a: number): string {
    const m = /^#([0-9a-f]{6})$/i.exec(color);
    if (!m) return color;
    const n = parseInt(m[1], 16);
    return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
  }

  function frame(t: number) {
    raf = 0;
    if (!el.isConnected) return;
    if (last) sweep = (sweep + ((t - last) / 1000) * ((Math.PI * 2) / 9)) % (Math.PI * 2);
    last = t;
    draw();
    if (!reduce.matches && !document.hidden) raf = requestAnimationFrame(frame);
  }
  function kick() {
    if (!raf) {
      last = 0;
      raf = requestAnimationFrame(frame);
    }
  }

  function resize() {
    const w = Math.max(280, Math.floor(el.clientWidth));
    dpr = window.devicePixelRatio || 1;
    W = w;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(w * dpr);
    canvas.style.height = w + "px";
    layout();
    kick();
  }
  const ro = new ResizeObserver(() => resize());
  ro.observe(el);
  const mq = () => kick();
  reduce.addEventListener("change", mq);
  document.addEventListener("visibilitychange", mq);
  const mo = new MutationObserver(() => kick());
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

  function hit(e: MouseEvent) {
    const r = canvas.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    let best: (typeof pos)[number] | null = null;
    let bd = 100;
    for (const p of pos) {
      const d = (p.x - x) ** 2 + (p.y - y) ** 2;
      if (d < bd) {
        bd = d;
        best = p;
      }
    }
    return { p: best, x, y };
  }
  canvas.addEventListener("mousemove", (e) => {
    const { p, x, y } = hit(e);
    hover = p ? p.b.k : null;
    canvas.style.cursor = p ? "pointer" : "default";
    if (p) {
      tip.textContent = `${p.b.name.toUpperCase()}  ${p.b.days}D`;
      tip.classList.remove("hidden");
      tip.style.left = Math.min(x + 12, W - 150) + "px";
      tip.style.top = y + 12 + "px";
    } else tip.classList.add("hidden");
    kick();
    if (reduce.matches) draw();
  });
  canvas.addEventListener("mouseleave", () => {
    hover = null;
    tip.classList.add("hidden");
    draw();
  });
  canvas.addEventListener("click", (e) => {
    const { p } = hit(e);
    if (p) onSelect(p.b.k);
  });

  resize();
  return {
    el,
    update(b) {
      blips = b;
      layout();
      kick();
    },
    select(k) {
      selected = k;
      kick();
      draw();
    },
    destroy() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
      reduce.removeEventListener("change", mq);
      document.removeEventListener("visibilitychange", mq);
    },
  };
}
